import { z } from "zod";

export const inviteUserRequestSchema = z.object({
  email: z.string().email(),
  role: z.enum(["admin", "member"]),
});

const enterpriseOrgPurchaseDates = z.object({
  purchase_millis: z.number(),
  expiry_millis: z.number(),
});

const creditsUpdateKindSchema = z.enum(["replace", "increment"]);

const creditsCycleConfigSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("monthly"), day: z.number().int().min(0).max(31) }),
  z.object({
    type: z.literal("quarterly"),
    first_month: z.number().int().min(1).max(3),
    day: z.number().int().min(1).max(31),
  }),
  z.object({
    type: z.literal("annual"),
    month: z.number().int().min(1).max(12),
    day: z.number().int().min(1).max(31),
  }),
]);

const enterpriseOrgCreditsRefillConfigSchema = z.object({
  cycleConfig: creditsCycleConfigSchema,
  updateKind: creditsUpdateKindSchema,
  amount: z.number().int().min(0),
});

const enterpriseOrgCreditsResetConfigSchema = z.object({
  enabled: z.boolean(),
  cycleConfig: creditsCycleConfigSchema,
});

export const enterpriseOrgSeatsCreditsConfigurationSchema = z.object({
  purchase_dates: enterpriseOrgPurchaseDates,
  credits_refill_config: enterpriseOrgCreditsRefillConfigSchema,
  credits_reset_config: enterpriseOrgCreditsResetConfigSchema,
  included_seat_count: z.number().int().min(1),
});

export const configureEnterpriseOrgSeatsCreditsRequestSchema =
  enterpriseOrgSeatsCreditsConfigurationSchema.extend({
    orgId: z.string(),
  });

export const enterpriseOrgSeatsCreditsRequestSchema = z.object({
  orgId: z.string(),
});

export const enterpriseOrgSeatsCreditsResponseSchema = enterpriseOrgSeatsCreditsConfigurationSchema;

export const configureEnterpriseOrgSeatsCreditsResponseSchema = z.object({
  success: z.boolean(),
  orgId: z.string(),
});

export const convertOrgToEnterpriseConfigSchema = z.object({
  purchase_dates: enterpriseOrgPurchaseDates,
  credits_refill_config: enterpriseOrgCreditsRefillConfigSchema,
  credits_reset_config: enterpriseOrgCreditsResetConfigSchema,
});

export const convertOrgToEnterpriseRequestSchema = convertOrgToEnterpriseConfigSchema.extend({
  orgId: z.string(),
});

export const convertOrgToEnterpriseResponseSchema = z.object({
  success: z.boolean(),
  orgId: z.string(),
});

export const inviteMembersOrgRequestSchema = z.object({
  orgId: z.string(),
  invites: z.array(inviteUserRequestSchema).min(1),
});

export const inviteMembersOrgResponseSchema = z.object({
  success: z.boolean(),
  orgId: z.string(),
});

export const createEnterpriseOrgRequestSchema = z.object({
  invites: z
    .array(inviteUserRequestSchema)
    .refine((invites) => invites.some((inv) => inv.role === "admin"), {
      message: "At least one admin invite is required",
    }),
  team_name: z.string().nonempty(),
  purchase_dates: enterpriseOrgPurchaseDates,
  credits_refill_config: enterpriseOrgCreditsRefillConfigSchema,
  credits_reset_config: enterpriseOrgCreditsResetConfigSchema,
});

export const createEnterpriseOrgResponseSchema = z.object({
  success: z.boolean(),
  orgId: z.string(),
});

export type CreditsCycleConfig = z.infer<typeof creditsCycleConfigSchema>;
export type CreditsUpdateKind = z.infer<typeof creditsUpdateKindSchema>;
export type EnterpriseOrgSeatsCreditsConfigurationModel = z.infer<
  typeof enterpriseOrgSeatsCreditsConfigurationSchema
>;
export type ConvertOrgToEnterpriseConfigModel = z.infer<typeof convertOrgToEnterpriseConfigSchema>;
export type InviteMembersOrgRequestModel = z.infer<typeof inviteMembersOrgRequestSchema>;
export type CreateEnterpriseOrgRequestModel = z.infer<typeof createEnterpriseOrgRequestSchema>;
export type EnterpriseOrgPurchaseDates = z.infer<typeof enterpriseOrgPurchaseDates>;
export type EnterpriseOrgCreditsResetConfigModel = z.infer<
  typeof enterpriseOrgCreditsResetConfigSchema
>;
export type EnterpriseOrgCreditsRefillConfigModel = z.infer<
  typeof enterpriseOrgCreditsRefillConfigSchema
>;
export type InviteUserRequestModel = z.infer<typeof inviteUserRequestSchema>;
